import { Box, Card, Typography, styled } from '@mui/material';
import { ReactNode } from 'react';
import { GlowingEffect } from './GlowingEffect';

interface GlowingCardProps {
	icon: ReactNode;
	title: string;
	description: string;
	onClick?: () => void;
	disabled?: boolean;
	minHeight?: string;
}

const StyledCard = styled(Card, {
	shouldForwardProp: (prop) => prop !== 'minHeight',
})<{ minHeight?: string }>(({ minHeight }) => ({
	position: 'relative',
	...(minHeight && { minHeight }),
	borderRadius: '1.5rem',
	width: '100%',
	border: 'none',
	padding: '0',
	cursor: 'pointer',
	transition: 'all 0.3s ease',
	background: 'transparent',
	boxShadow: 'none',

	'&:hover': {
		transform: 'translateY(-4px)',
	},

	'@media (min-width: 768px)': {
		borderRadius: '1.5rem',
		...(minHeight && { minHeight }),
	},
}));

const InnerCard = styled(Box)(() => ({
	position: 'relative',
	height: '100%',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	gap: '20px',
	overflow: 'hidden',
	borderRadius: '20px',
	border: '1px solid rgba(71, 134, 255, 0.15)',
	background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
	padding: '28px',
	boxShadow: '0px 4px 20px rgba(71, 134, 255, 0.08)',
	zIndex: 1,

	'@media (min-width: 768px)': {
		padding: '32px',
		gap: '24px',
	},
}));

const IconContainer = styled(Box)(() => ({
	width: '48px',
	height: '48px',
	borderRadius: '12px',
	border: '1px solid rgba(71, 134, 255, 0.2)',
	backgroundColor: 'rgba(71, 134, 255, 0.08)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,

	'@media (min-width: 768px)': {
		width: '56px',
		height: '56px',
		borderRadius: '16px',
	},
}));

const TitleTypography = styled(Typography)(() => ({
	fontSize: '18px',
	lineHeight: '1.4',
	fontWeight: 700,
	letterSpacing: '-0.02em',
	color: '#102142',
	marginBottom: '8px',

	'@media (min-width: 768px)': {
		fontSize: '20px',
		lineHeight: '1.3',
		marginBottom: '12px',
	},
}));

const DescriptionTypography = styled(Typography)(() => ({
	fontSize: '14px',
	lineHeight: '1.5',
	color: '#6B7280',
	display: '-webkit-box',
	WebkitLineClamp: 3,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',

	'@media (min-width: 768px)': {
		fontSize: '15px',
		lineHeight: '1.6',
	},
}));

export const GlowingCard = ({ 
	icon, 
	title, 
	description, 
	onClick, 
	disabled = false,
	minHeight 
}: GlowingCardProps) => {
	return (
		<StyledCard minHeight={minHeight} onClick={onClick}>
			<GlowingEffect
				spread={60}
				glow={!disabled}
				disabled={disabled}
				proximity={100}
				inactiveZone={0.3}
				borderWidth={2}
				blur={4}
			/>
			<InnerCard>
				<IconContainer sx={{ 
					opacity: disabled ? 0.5 : 1,
					transition: 'all 0.3s ease'
				}}>
					{icon}
				</IconContainer>
				<Box sx={{ 
					display: 'flex', 
					flexDirection: 'column',
					flex: 1
				}}>
					<TitleTypography sx={{ 
						opacity: disabled ? 0.7 : 1,
						transition: 'all 0.3s ease'
					}}>
						{title}
					</TitleTypography>
					<DescriptionTypography sx={{ 
						opacity: disabled ? 0.6 : 1,
						transition: 'all 0.3s ease'
					}}>
						{description}
					</DescriptionTypography>
				</Box>
			</InnerCard>
		</StyledCard>
	);
};